import { Button, formatNumber, useEffect, useState } from "@";
import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useInfiniteZipFilesQuery } from "../api/queries";
import type { ZipFile } from "../types.ts";

interface PreviousZipFilesPanelProps {
  selectedKey: string | null;
  onSelect: (fileKey: string | null) => void;
  enabled: boolean;
}

const SkeletonRow = () => (
  <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-3 dark:border-gray-700/60 dark:bg-gray-800">
    <div className="h-9 w-9 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      <div className="h-3 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
    </div>
  </div>
);

const PreviousZipFilesPanel = ({
  selectedKey,
  onSelect,
  enabled,
}: PreviousZipFilesPanelProps) => {
  const [name, setName] = useState("");
  const [debouncedName, setDebouncedName] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(name.trim()), 300);
    return () => clearTimeout(t);
  }, [name]);

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteZipFilesQuery(debouncedName, enabled);

  const files: ZipFile[] =
    data?.pages.flatMap((page) => page.data ?? []) ?? [];
  const totalItems = data?.pages?.[0]?.totalItems ?? 0;

  return (
    <div className="space-y-3">
      <div className="relative">
        <span
          aria-hidden
          className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
        >
          <MagnifyingGlassIcon className="h-5 w-5" />
        </span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ابحث باسم الملف"
          dir="rtl"
          className="input pe-10"
        />
      </div>

      {files.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatNumber(totalItems)} ملف
        </p>
      )}

      <div className="max-h-72 space-y-2 overflow-y-auto pe-1">
        {isLoading && enabled && files.length === 0 ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : files.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-200 px-6 py-10 text-center dark:border-gray-700/60">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
              <ArchiveBoxIcon className="h-5 w-5" />
            </span>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {debouncedName
                ? "لا توجد ملفات مطابقة"
                : "لا توجد ملفات سابقة"}
            </p>
          </div>
        ) : (

          <div className="grid grid-cols-2 gap-2">
{
          files.map((file) => {
            const isSelected = file.fileKey === selectedKey;
            return (
              <button
                key={file.fileKey}
                type="button"
                onClick={() => onSelect(isSelected ? null : file.fileKey)}
                className={`group flex w-full items-center gap-3 rounded-2xl border p-3 text-start transition ${
                  isSelected
                    ? "border-green-300 bg-green-50 ring-1 ring-green-200 dark:border-green-700/60 dark:bg-green-950/30 dark:ring-green-800/40"
                    : "border-gray-100 bg-white hover:border-green-200 hover:bg-green-50/40 dark:border-gray-700/60 dark:bg-gray-800 dark:hover:border-green-700/40 dark:hover:bg-green-950/20"
                }`}
              >
                <span
                  aria-hidden
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                    isSelected
                      ? "bg-green-700 text-white dark:bg-green-600"
                      : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                  }`}
                >
                  <ArchiveBoxIcon className="h-5 w-5" />
                </span>
                <span
                  className="flex-1 truncate text-sm font-medium tabular-nums text-gray-800 dark:text-gray-100"
                  title={file.fileName}
                >
                  {file.fileName}
                </span>
                {isSelected && (
                  <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-700 dark:text-green-400" />
                )}
              </button>
            );
          })
        }
          </div>
        
        )}
      </div>

      <div className="flex items-center justify-center pt-1">
        {hasNextPage ? (
          <Button
            type="button"
            title="تحميل المزيد"
            isLoading={isFetchingNextPage}
            onClick={() => fetchNextPage()}
            className="btn-outline-green !w-fit px-6"
          />
        ) : (
          files.length > 0 &&
          !isFetching && (
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-300 dark:text-gray-600">
              نهاية القائمة
            </span>
          )
        )}
      </div>
    </div>
  );
};

export default PreviousZipFilesPanel;
